import { Children, cloneElement, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import { TransitionProps } from "@mui/material/transitions";

import { BannerType, BannerView } from "@dotnet/telematics-design-system";

import { FILE_ACTIONS } from "components/Actions/types/enums";
import Dialog, {
    CombinedDialogProps
} from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import Tag, { TAG_COLORS, TAG_SIZES } from "components/DesignComponents/Tag";
import AlertContext from "context/alert/alertContext";
import TableContext from "context/table/tableContext";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { TRI_SOURCES } from "shared/types/enums";

import { DialogWithConfirmationProps } from "./types";

const DialogWithConfirmation = ({
    count,
    confirmationDescription,
    source,
    isBulkActionLoading,
    ...props
}: DialogWithConfirmationProps & CombinedDialogProps) => {
    const { removeAlertForBGAction } = useContext(AlertContext);
    const { tableName } = useContext(TableContext);

    const { t } = useTranslation();
    const { renderFormattedNumber } = useFormattedNumber();

    const [needConfirmation, setNeedConfirmation] = useState(count > 20);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [enteredNumber, setEnteredNumber] = useState("");

    const isConfirmed = enteredNumber.trim() === String(count);

    useEffect(() => {
        setNeedConfirmation(count > 20);
    }, [count]);

    const handleSubmit = () => {
        removeAlertForBGAction();

        if (needConfirmation) {
            setShowConfirmation(true);
        } else {
            const { submit } = props;

            return submit?.();
        }
    };

    const handleEnteredNumberChange = (e: { target: { value: any } }) => {
        const value = e.target.value.trim();

        setEnteredNumber(value);
        setNeedConfirmation(value !== String(count));
    };

    const sourceCheck = () => {
        if (source === TRI_SOURCES.FromFile) {
            return {
                tagTitle: `${t(
                    "Dialog##confirmations##number of lines"
                )}: ${count}`,
                inputLabel: t("Dialog##confirmations##file")
            };
        } else {
            return {
                tagTitle: `${t(
                    `Dialog##selected ${tableName}`
                )}: ${renderFormattedNumber(count)}`,
                inputLabel: t(`Dialog##confirmations##${source}##${tableName}`)
            };
        }
    };

    const { tagTitle, inputLabel } = sourceCheck();

    return (
        <Dialog
            {...props}
            isTitleSeparator
            isActionsSeparator
            title={
                showConfirmation
                    ? t("Dialog##confirmations##are you sure")
                    : props.title
            }
            description={
                showConfirmation ? confirmationDescription : props.description
            }
            close={isBulkActionLoading ? undefined : props.close}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: node => {
                        removeAlertForBGAction();
                        setShowConfirmation(false);

                        const transitionProps = props.slotProps
                            ?.transition as TransitionProps;

                        transitionProps.onExited?.(node);
                    }
                }
            }}
            actions={Children.map(props.actions.props.children, child => {
                const isSubmitBtn = child.props.type === "submit";

                return cloneElement(child, {
                    css: [
                        child.props.css,
                        {
                            transition: "unset",
                            "&:hover": {
                                transition:
                                    "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms"
                            }
                        }
                    ],
                    ...(showConfirmation && isSubmitBtn
                        ? {
                              disabled: !isConfirmed
                          }
                        : {})
                });
            })}
        >
            <div
                css={css({
                    padding: "8px 0"
                })}
            >
                {props.title.toLowerCase() === FILE_ACTIONS.DeleteFiles && (
                    <BannerView
                        bannerType={BannerType.Warning}
                        title={t("Dialog##delete file warning")}
                        css={css({
                            marginBottom: "16px"
                        })}
                    />
                )}

                {showConfirmation ? (
                    <>
                        <Tag
                            size={TAG_SIZES.Medium}
                            color={TAG_COLORS.Primary}
                            title={tagTitle}
                            css={css({
                                marginBottom: "8px"
                            })}
                        />

                        <InputField
                            fullWidth
                            required
                            size={INPUT_FIELD_SIZES.Medium}
                            value={enteredNumber}
                            placeholder="0"
                            onChange={handleEnteredNumberChange}
                            labelLeft={inputLabel}
                        />
                    </>
                ) : (
                    props.children
                )}
            </div>
        </Dialog>
    );
};

export default DialogWithConfirmation;
